namespace advisorop.components.widgets {
    "use strict"

    // The one Salesforce case list. Three dashboards show it and they differ only in which
    // cases they ask Salesforce for - the columns, the status selector, the panel chrome and
    // both popups are the same everywhere:
    //
    //   advisor    the signed-in advisor's cases, or everyone's while admin mode is on
    //   servicer   every case in the org; a servicer works the whole book
    //   household  the cases Salesforce holds for one household's people
    //
    // Set scope accordingly; household also needs household-id.
    export class SalesforceCasesController {

        scope: string;
        householdId: number;
        // Optional line under the grid. The panel shows a footer only when this is set.
        footerText: string;

        cases: domains.ISalesforceCaseTrackerData[] = [];
        instanceUrl: string = "";
        totalUrl: string = "";
        // Household scope only: the Salesforce household the cases hang off, so "Add case" can
        // be opened against it. Null when the investors sit under more than one, or none.
        salesforceHousehold: domains.ISalesforceLookupResult = null;

        loading = true;

        // Salesforce is queried per status rather than the grid being filtered on screen, so
        // what is shown is always the whole set for that status. Open is the default.
        statusFilter: string = "open";
        statusOptions: any[];

        // Advisor scope only. Admin mode widens the list to every advisor's cases.
        filterByAdvisor = 1;

        dgCases: DevExpress.ui.dxDataGridOptions;
        loadOptions: DevExpress.ui.dxLoadPanelOptions;

        static $inject = ["generalService", "advisorService", "salesforceCaseApi",
            "salesforceCaseGrid", "$rootScope"];
        constructor(private gs: advisorop.services.GeneralService,
            private as: advisorop.services.AdvisorService,
            private api: advisorop.services.SalesforceCaseApi,
            private grid: advisorop.services.SalesforceCaseGrid,
            private $rootScope: ng.IRootScopeService) {

            this.dgCases = this.grid.buildGrid("vm.cases",
                c => this.grid.openCaseDetail(c, this.instanceUrl));
            this.loadOptions = this.grid.buildLoadPanel("#salesforceCases");
            this.statusOptions = this.grid.statusOptions;
        }

        private isHousehold(): boolean { return this.scope === "household"; }

        // Which controller the case screens talk to. Only a servicer has their own; the
        // household list is an advisor screen.
        private apiScope(): advisorop.services.SalesforceCaseScope {
            return this.scope === "servicer" ? "servicer" : "advisor";
        }

        private setFilterByAdvisor(): void {
            const adminMode = this.as.getAdminMode();
            this.filterByAdvisor = (adminMode && adminMode.enabled) ? 0 : 1;
        }

        emptyText(): string {
            const where = this.isHousehold() ? " for this household" : "";

            if (this.statusFilter === "closed") return `No closed cases in Salesforce${where}.`;
            if (this.statusFilter === "all") return `No cases in Salesforce${where}.`;
            return `No open cases in Salesforce${where}.`;
        }

        loadCases(): void {
            // The dashboard resolves the household after the component is bound, so the first
            // change can arrive without an id.
            if (this.isHousehold() && !this.householdId) return;

            this.loading = true;

            const request = this.isHousehold()
                ? this.api.getHouseholdCases(this.householdId, this.statusFilter)
                : this.api.getCases(this.apiScope(), this.statusFilter, this.filterByAdvisor == 1);

            request.then(
                resp => {
                    this.cases = resp.data.cases;
                    this.instanceUrl = resp.data.instanceUrl;
                    this.totalUrl = resp.data.totalUrl;
                    this.salesforceHousehold = resp.data.household;
                },
                err => this.gs.showError(err))
                .finally(() => this.loading = false);
        }

        // Opens the "Add case" form. Everything it offers is read from Salesforce when the
        // modal loads, so a case type or picklist changed in the org shows up here without a
        // release on our side. On a household the household is filled in for them.
        openAddCasePopup(): void {
            const household = this.isHousehold() ? this.salesforceHousehold : null;

            this.grid.openAddCase(this.apiScope(), household).then(
                () => {
                    this.loadCases();
                    // The tiles on the advisor dashboard count the same cases, so they have to
                    // pick up the new one as well.
                    this.$rootScope.$broadcast("salesforceCaseCreated");
                },
                // Dismissing the modal is not an error.
                () => { });
        }

        $onChanges(changes): void {
            if (this.isHousehold() && changes.householdId) this.loadCases();
        }

        $onInit(): void {
            if (this.isHousehold()) return;   // waits for its household id instead

            if (this.scope !== "servicer") {
                this.setFilterByAdvisor();

                this.$rootScope.$on("adminModeChanged", () => {
                    this.setFilterByAdvisor();
                    this.loadCases();
                });
            }

            this.loadCases();
        }
    }

    class SalesforceCases implements ng.IComponentOptions {

        public bindings: any;
        public controller: any;
        public controllerAs: string;
        public templateUrl: string;

        constructor() {
            this.bindings = {
                scope: "@",
                householdId: "<",
                footerText: "@"
            };
            this.controller = SalesforceCasesController;
            this.controllerAs = "vm";
            this.templateUrl = "spa/shared/common/salesforce-cases.html";
        }
    }
    angular.module(Constants.APP).component("salesforceCases", new SalesforceCases());
}
